import React, { useState, useEffect } from 'react';
import { TodueWordmark } from '@/components/Logo';
import ThemeToggle from '@/components/ThemeToggle';
import SectionIcon from '@/components/SectionIcon';
import { I } from '@/lib/icons';

// Prezentačné ľavé menu (desktop). Jeden vizuál pre produkciu (V1 Sidebar) aj
// app2 (V2Shell) — 1:1 garantované, líšia sa len dátami cez props. Žiadne hooky
// tu: dáta (sekcie + počty), vyhľadávanie, pinned a prepínač účtu dodáva wrapper
// ako props/sloty, aby offline app2 nemusela ťahať Supabase/workspace coupling.
export default function SidebarNav({
  sections = [],
  groups = null,
  isActive = () => false,
  onNavigate = () => {},
  brandHeight = 36,
  searchSlot = null,
  pinnedSlot = null,
  accountSlot = null,
  onBrandClick = null,
  testIdPrefix = 'sidebar',
}) {
  // Jeden item-button — zdieľaný plochou (sections) aj skupinovou (groups) vetvou.
  // Vizuál/testid/hover/active/count IDENTICKÝ v oboch — žiadna duplicita.
  const renderItem = (s) => {
    const active = isActive(s.path);
    return (
      <button key={s.id} data-testid={`${testIdPrefix}-${s.id}`} onClick={() => onNavigate(s.path)}
        style={{
          display: 'flex', alignItems: 'center', gap: 10, padding: '6px 10px',
          border: 'none', textAlign: 'left',
          background: active ? `var(--sec-${s.color}-soft)` : 'transparent',
          color: active ? `var(--sec-${s.color})` : 'var(--text-primary)',
          borderRadius: 'var(--r-sm)', cursor: 'pointer',
          fontWeight: active ? 600 : 500, fontFamily: 'inherit',
        }}
        onMouseEnter={e => { if (!active) e.currentTarget.style.background = 'var(--bg-hover)'; }}
        onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent'; }}
      >
        <SectionIcon color={s.color} size={22}>
          {React.createElement(I[s.icon] || I.doc, { width: 13, height: 13 })}
        </SectionIcon>
        <span style={{ fontSize: 13, flex: 1 }}>{s.label}</span>
        {s.count != null && (
          <span style={{ fontSize: 12, color: 'var(--text-muted)', fontVariantNumeric: 'tabular-nums' }}>
            {s.count}
          </span>
        )}
      </button>
    );
  };

  // Rovnaký štýl ako pôvodná "Moje sekcie" hlavička — zdieľaný oboma vetvami.
  const headingStyle = {
    fontSize: 11, fontWeight: 600, color: 'var(--text-muted)',
    padding: '4px 10px 6px', letterSpacing: '0.02em',
  };

  const useGroups = Array.isArray(groups) && groups.length > 0;

  // Skupinu zobrazíme len ak má aspoň jednu položku ALEBO vlastný board
  // (rozcestník). Skupina s boardom je klikateľná aj bez položiek.
  const visibleGroups = useGroups
    ? groups.filter(g => (Array.isArray(g.items) && g.items.length > 0) || g.board)
    : [];

  // Ktorá skupina obsahuje aktuálnu aktívnu cestu (jej board alebo niektorý item).
  const groupIsActive = (g) =>
    (g.board && isActive(g.board)) ||
    (Array.isArray(g.items) && g.items.some(it => isActive(it.path)));

  // Akordeón: otvorená je LEN JEDNA skupina naraz. Default = tá s aktívnou cestou;
  // keď žiadna skupina nie je aktívna (Prehľad, Klienti, Poradca, Nastavenia —
  // standalone položky mimo akordeónu), padni na prvú skupinu s položkami namiesto
  // "null" (=celé menu zbalené, vidno len standalone položky). BUGFIX 2026-08-07:
  // po oprave root-route "/" → "/v2/prehlad" (App.js) sa presne tento null-stav stal
  // živým — Faktúry/Výdavky/Doklady/Reporting boli neviditeľné pod zbalenou hlavičkou,
  // vidno bolo len Klienti. Fable verdikt: fallback = prvá visibleGroup s items (nie
  // hardcoded 'FINANCIE'), nech ostane korektné aj pri preusporiadaní skupín.
  const activeGroupTitle = visibleGroups.find(groupIsActive)?.title ?? null;
  const fallbackGroupTitle = visibleGroups.find(g => Array.isArray(g.items) && g.items.length > 0)?.title ?? null;
  const defaultOpenGroup = activeGroupTitle ?? fallbackGroupTitle;
  const [openGroup, setOpenGroup] = useState(defaultOpenGroup);

  // Re-sync keď sa aktívna cesta zmení zvonku (navigácia mimo menu otvorí správnu
  // skupinu). Musí použiť ROVNAKÝ fallback ako initializer vyššie — inak sa menu
  // zbalí znova pri každom prechode na standalone stránku (napr. klik na logo).
  useEffect(() => {
    setOpenGroup(defaultOpenGroup);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultOpenGroup]);

  // Klik na hlavičku skupiny: vždy otvor danú (ostatné zbalí) a ak má board,
  // naviguj naň. Opätovný klik na už otvorenú BEZ boardu ju zbalí.
  const handleGroupClick = (g) => {
    const isOpen = openGroup === g.title;
    if (g.board) {
      setOpenGroup(g.title);
      onNavigate(g.board);
    } else {
      setOpenGroup(isOpen ? null : g.title);
    }
  };

  return (
    <aside className="cd-sidebar" style={{
      width: 260, background: 'var(--bg-alt)',
      borderRight: '1px solid var(--border)',
      display: 'flex', flexDirection: 'column', flexShrink: 0,
    }}>
      {/* Brand header — logo klikateľné (→ Prehľad) len keď je onBrandClick zadaný */}
      <div style={{ padding: '14px 12px 6px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
        {onBrandClick ? (
          <div
            role="button"
            tabIndex={0}
            data-testid={`${testIdPrefix}-brand`}
            onClick={onBrandClick}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onBrandClick(); } }}
            style={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer', background: 'transparent', border: 'none', padding: 0 }}
          >
            <TodueWordmark height={brandHeight} />
          </div>
        ) : (
          <TodueWordmark height={brandHeight} />
        )}
        <ThemeToggle size={16} />
      </div>

      {/* Search */}
      {searchSlot != null && (
        <div style={{ padding: '20px 8px 10px' }}>
          {searchSlot}
        </div>
      )}

      {/* Section list */}
      <div className="cd-scroll" style={{ padding: '4px 8px 8px', flex: 1, overflowY: 'auto' }}>
        {useGroups ? (
          // Skupinová vetva (app2 V2Shell): akordeón — otvorená je LEN JEDNA skupina.
          // Hlavička = klikateľný button (title + chevron). Podpoložky sa renderujú
          // len keď je skupina otvorená. Skupina s boardom je klikateľná aj bez items.
          visibleGroups.map(g => {
            const isOpen = openGroup === g.title;
            const active = groupIsActive(g);
            return (
              <div key={g.title}>
                <button
                  type="button"
                  data-testid={`${testIdPrefix}-group-${g.title}`}
                  onClick={() => handleGroupClick(g)}
                  aria-expanded={isOpen}
                  style={{
                    ...headingStyle,
                    display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                    border: 'none', textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit',
                    background: active ? 'var(--bg-hover)' : 'transparent',
                    color: active ? 'var(--text-primary)' : 'var(--text-muted)',
                    borderRadius: 'var(--r-sm)',
                  }}
                  onMouseEnter={e => { if (!active) e.currentTarget.style.background = 'var(--bg-hover)'; }}
                  onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent'; }}
                >
                  <span style={{ flex: 1 }}>{g.title}</span>
                  <span aria-hidden="true" style={{ fontSize: 10, lineHeight: 1 }}>{isOpen ? '▾' : '▸'}</span>
                </button>
                {isOpen && Array.isArray(g.items) && g.items.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {g.items.map(renderItem)}
                  </div>
                )}
              </div>
            );
          })
        ) : (
          // Plochá vetva (V1 prod Sidebar) — 1:1 s pôvodným správaním.
          <>
            <div style={headingStyle}>Moje sekcie</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {sections.map(renderItem)}
            </div>
          </>
        )}

        {/* Pinned clients (voliteľné — dodá wrapper) */}
        {pinnedSlot}
      </div>

      {/* Account switcher / pätka (voliteľné — dodá wrapper) */}
      {accountSlot}
    </aside>
  );
}
